/**
 * Uptime history.
 *
 * The screen customers ask about, and the one whose numbers are easiest to
 * render dishonestly. Three rules govern everything below.
 *
 * ── A missing answer is never drawn as a number ─────────────────────────
 *
 * `uptime: null` means nothing definite was learned. It renders as "no answer"
 * in the stale colour, never as 0% and never as 100%. A green bar over zero
 * definite readings is the single worst thing this page could show: it is the
 * predecessor's dead-watcher green row, redrawn as a percentage.
 *
 * ── The evidence is shown next to the number ────────────────────────────
 *
 * "100%" over six readings and over fourteen hundred are the same number and
 * different claims. Every row carries what it was computed from and how much
 * the cadence implies, so a thin figure looks thin. A summary elsewhere in this
 * codebase counted pass/fail/unknown and dropped `unavailable`, which made a
 * check that could never be answered read as "7 readings · 0 · 0 · 0".
 *
 * ── Silence is shown, because nothing records it ────────────────────────
 *
 * If the collector stops, rows stop, and a ratio over the rows that survive
 * reports a clean sheet for the period nobody was watching. The trailing
 * silence column is the only thing on this page that can contradict a green
 * percentage, so it is not tucked behind a toggle.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';

import { api } from './api.ts';
import {
  Banner,
  DataTable,
  Empty,
  Loading,
  Pill,
  Section,
  Toolbar,
  type ColumnDef,
} from './components/index.ts';
import {
  RATEABLE_READINGS,
  band,
  coverage,
  formatDuration,
  formatUptime,
  silenceThresholdMs,
} from './uptime-state.ts';

const DAY_MS = 86_400_000;

/** "14 days", "1 day", "0.4 days" — whichever reads as a real quantity. */
function formatDays(days: number): string {
  if (days < 1) return `${Math.max(0.1, Math.round(days * 10) / 10)} days`;
  const whole = Math.round(days);
  return whole === 1 ? '1 day' : `${whole} days`;
}

interface CheckKind {
  key: string;
  label: string;
  default_interval_s: number;
}

interface UptimeRow {
  id: string;
  name: string;
  code: string | null;
  kind: string;
  driver_id: string | null;
  site_code: string | null;
  site_name: string | null;
  pass: number;
  fail: number;
  unknown: number;
  unavailable: number;
  definite: number;
  total: number;
  uptime: number | null;
  expectedReadings: number | null;
  trailingSilenceMs: number;
  firstObservedAt: string | null;
}

/**
 * How much of the requested window has data behind it.
 *
 * A 90-day request answered from 14 days of history is the failure this screen
 * exists not to repeat. See the route comment in `src/api/server.ts`.
 */
interface Coverage {
  requestedDays: number;
  coveredDays: number;
  oldestObservedAt: string | null;
  short: boolean;
}

interface UptimeResponse {
  window: {
    days: number;
    checkKey: string;
    intervalSeconds: number | null;
    coverage: Coverage;
  };
  checkKinds: CheckKind[];
  assets: UptimeRow[];
}

/**
 * The caveat under the figure. Silence first: it is the one that can make a
 * green percentage a lie rather than merely a weak claim. Null when the
 * figure stands on its own.
 */
function caveatFor(row: UptimeRow, silenceThreshold: number): string | null {
  const blind = row.trailingSilenceMs > silenceThreshold;
  if (blind && row.total > 0) return `nothing recorded for ${formatDuration(row.trailingSilenceMs)}`;
  if (!blind && row.uptime !== null && row.definite < RATEABLE_READINGS) {
    return `only ${row.definite} definite readings — too few for the ratio to mean much`;
  }
  return null;
}

export function UptimeView(): React.JSX.Element {
  const [days, setDays] = useState(30);
  const [checkKey, setCheckKey] = useState('reachable');
  const [data, setData] = useState<UptimeResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setData(
        await api.get<UptimeResponse>(
          `/api/uptime?days=${days}&check=${encodeURIComponent(checkKey)}`,
        ),
      );
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'could not load uptime');
    } finally {
      setLoading(false);
    }
  }, [days, checkKey]);

  useEffect(() => {
    void load();
  }, [load]);

  const windowMs = (data?.window.days ?? days) * DAY_MS;
  const silenceThreshold = silenceThresholdMs(windowMs);

  const columns = useMemo<ColumnDef<UptimeRow, unknown>[]>(
    () => [
      {
        id: 'asset',
        header: 'Asset',
        accessorFn: (r) => r.code ?? r.name,
        cell: ({ row }) => (
          <>
            <div>{row.original.name}</div>
            <div className="cell-sub">{row.original.code ?? row.original.kind}</div>
          </>
        ),
      },
      {
        id: 'site',
        header: 'Site',
        accessorFn: (r) => r.site_code ?? '',
        cell: ({ getValue }) => <span className="small muted">{(getValue() as string) || '—'}</span>,
      },
      {
        id: 'uptime',
        header: 'Uptime',
        accessorFn: (r) => r.uptime ?? -1,
        meta: { align: 'right' },
        cell: ({ row }) => {
          const caveat = caveatFor(row.original, silenceThreshold);
          return (
            <>
              <Pill state={band(row.original, silenceThreshold)} reason={caveat}>
                {formatUptime(row.original.uptime)}
              </Pill>
              {caveat !== null && <div className="small caveat">{caveat}</div>}
            </>
          );
        },
      },
      {
        id: 'pass',
        header: 'Pass',
        accessorKey: 'pass',
        meta: { align: 'right' },
        cell: ({ getValue }) => <span className="mono">{getValue() as number}</span>,
      },
      {
        id: 'fail',
        header: 'Fail',
        accessorKey: 'fail',
        meta: { align: 'right' },
        cell: ({ getValue }) => <span className="mono">{getValue() as number}</span>,
      },
      /*
        `unknown` and `unavailable` get their own columns rather than a shared
        "other". They mean opposite things — one is a check that could not get
        an answer, the other a device that can never give one — and the
        operator response to each differs.
      */
      {
        id: 'unknown',
        header: 'No answer',
        accessorKey: 'unknown',
        meta: { align: 'right' },
        cell: ({ getValue }) => <span className="mono muted">{getValue() as number}</span>,
      },
      {
        id: 'unavailable',
        header: 'Not askable',
        accessorKey: 'unavailable',
        meta: { align: 'right' },
        cell: ({ getValue }) => <span className="mono muted">{getValue() as number}</span>,
      },
      /*
        Coverage as a figure, because "4319 / 21600" is a division the reader
        should not have to do to learn we saw a fifth of what we expected.
      */
      {
        id: 'readings',
        header: 'Readings',
        accessorFn: (r) => coverage(r) ?? -1,
        meta: { align: 'right' },
        cell: ({ row }) => {
          const seen = coverage(row.original);
          return (
            <div className="small mono">
              <span className="muted">
                {row.original.total}
                {row.original.expectedReadings !== null && ` / ${row.original.expectedReadings}`}
              </span>
              {seen !== null && (
                <div className={seen < 0.5 ? 'caveat' : 'muted'}>{Math.round(seen * 100)}% observed</div>
              )}
            </div>
          );
        },
      },
      {
        id: 'lastSeen',
        header: 'Last seen',
        accessorKey: 'trailingSilenceMs',
        meta: { align: 'right' },
        cell: ({ row }) => {
          const blind = row.original.trailingSilenceMs > silenceThreshold;
          const label =
            row.original.total === 0 ? 'never' : `${formatDuration(row.original.trailingSilenceMs)} ago`;
          return blind ? (
            <Pill state="fault" reason="Longer than a tenth of the window — nobody was watching.">
              {label}
            </Pill>
          ) : (
            <span className="small muted">{label}</span>
          );
        },
      },
    ],
    [silenceThreshold],
  );

  if (error !== null) return <Banner tone="fault">{error}</Banner>;
  if (data === null) return <Loading rows={8} cols={9} label="Loading uptime" />;

  const answered = data.assets.filter((row) => row.uptime !== null);
  const unanswered = data.assets.length - answered.length;
  const windowLabel = data.window.coverage.short
    ? `the ${formatDays(data.window.coverage.coveredDays)} available`
    : `${data.window.days} days`;

  /*
   * The denominator is the SCHEDULE over the window asked for, and stays that
   * way even when coverage is short — otherwise an asset that was silent for
   * the first half of the window would have its target quietly lowered to
   * match, and a real outage would render as a full record. The banner is
   * where thin history is explained; the "Readings" column is about the
   * schedule.
   */
  const summary =
    `${answered.length} of ${data.assets.length} assets have a definite answer over ${windowLabel}` +
    (unanswered > 0 ? ` · ${unanswered} gave none and read "no answer", which is not 100%` : '') +
    (data.window.intervalSeconds !== null
      ? ` · runs every ${data.window.intervalSeconds}s, about ${Math.round(windowMs / 1000 / data.window.intervalSeconds)} readings expected`
      : '');

  return (
    <div className="stack">
      <Toolbar
        {...(loading ? { end: <span className="small muted" role="status">Updating…</span> } : {})}
      >
        <label className="small muted" htmlFor="uptime-check">
          Check
        </label>
        <select id="uptime-check" value={checkKey} onChange={(event) => setCheckKey(event.target.value)}>
          {data.checkKinds.map((kind) => (
            <option key={kind.key} value={kind.key}>
              {kind.label}
            </option>
          ))}
        </select>
        <label className="small muted" htmlFor="uptime-days">
          Window
        </label>
        <select id="uptime-days" value={days} onChange={(event) => setDays(Number(event.target.value))}>
          <option value={7}>7 days</option>
          <option value={30}>30 days</option>
          <option value={90}>90 days</option>
        </select>
      </Toolbar>

      {data.window.coverage.short && (
        /*
         * Not a styling flourish. The number beside it was computed from
         * whatever history exists, and without this line the heading says
         * "90 days" over it. Rendered before the table rather than after,
         * because a caveat below a figure is read second or not at all.
         */
        <Banner
          tone="warn"
          title={
            data.window.coverage.coveredDays === 0
              ? 'No history for this window.'
              : `Only ${formatDays(data.window.coverage.coveredDays)} of history.`
          }
        >
          You asked for {data.window.days} days. Every figure below is computed from what exists,
          not from the window requested
          {data.window.coverage.oldestObservedAt !== null && (
            <> — the oldest reading is from {new Date(data.window.coverage.oldestObservedAt).toLocaleDateString()}</>
          )}
          .
        </Banner>
      )}

      <Section
        flush
        title="Uptime by asset"
        summary={`Passing over definite readings; readings with no answer are counted apart, never as pass or fail. ${summary}.`}
      >
        <DataTable
          data={data.assets}
          columns={columns}
          getRowId={(r) => r.id}
          initialSort={[{ id: 'uptime', desc: false }]}
          rowClass={(r) => (r.trailingSilenceMs > silenceThreshold && r.total > 0 ? 'row-fault' : undefined)}
          caption="Uptime by asset"
          empty={<Empty title="No assets in this tenant" />}
        />
      </Section>
    </div>
  );
}
